// frontend/src/services/reports.ts
import api from './api.ts';

// Interfaces para os dados de relatórios
export interface ClinicReport {
  clinic_id: number;
  clinic_name?: string;
  messages_count: number;
  leads_count: number;
  contacts_count: number;
}

export interface MonthlyReportResponse {
  year_month: string;
  total_messages: number;
  total_leads: number;
  total_contacts: number;
  clinics: ClinicReport[];
  generated_at: string;
}

export interface ReportsSummary {
  period_analyzed: string;
  total_messages: number;
  total_leads: number;
  average_messages_per_month: number;
  average_leads_per_month: number;
  monthly_breakdown: Array<{
    year_month: string;
    messages: number;
    leads: number;
  }>;
  generated_at: string;
}

export interface ReportFilters {
  year_month?: string;
  months?: number;
}

export interface UsageAutomationOverview {
  key: string;
  label: string;
  steps: number;
}

export interface UsageOverviewResponse {
  scope: 'historical' | string;
  messages: number;
  leads: number;
  contacts: number;
  whatsapp_connections: number;
  automations: UsageAutomationOverview[];
  clinic_filter?: number | null;
  generated_at: string;
}

/**
 * Busca relatório mensal de mensagens, leads e contatos por clínica
 * @param filters - Filtros opcionais para a consulta
 * @returns Promise<MonthlyReportResponse> - Dados do relatório mensal
 */
export const getMonthlyReport = async (filters: ReportFilters = {}): Promise<MonthlyReportResponse> => {
  try {
    console.log('[Reports API] Buscando relatório mensal com filtros:', filters);

    const params = new URLSearchParams();
    if (filters.year_month) {
      params.append('year_month', filters.year_month);
    }

    // Adiciona clinic_id do localStorage para filtrar pela clínica atual
    const clinicId = localStorage.getItem('clinic_id');
    if (clinicId) {
      params.append('clinic_id', clinicId);
      console.log('[Reports API] Filtrando por clinic_id:', clinicId);
    }

    const response = await api.get(`/api/reports/monthly?${params.toString()}`);
    console.log('[Reports API] Relatório mensal recebido:', response.data);

    return response.data;
  } catch (error) {
    console.error('[Reports API] Erro ao buscar relatório mensal:', error);
    throw error;
  }
};

/**
 * Busca resumo dos relatórios dos últimos N meses
 * @param months - Número de meses para analisar (1-12)
 * @returns Promise<ReportsSummary> - Resumo dos relatórios
 */
export const getReportsSummary = async (months: number = 6): Promise<ReportsSummary> => {
  try {
    console.log('[Reports API] Buscando resumo dos últimos', months, 'meses');

    const params = new URLSearchParams();
    params.append('months', months.toString());

    // Adiciona clinic_id do localStorage para filtrar pela clínica atual
    const clinicId = localStorage.getItem('clinic_id');
    if (clinicId) {
      params.append('clinic_id', clinicId);
      console.log('[Reports API] Filtrando por clinic_id:', clinicId);
    }

    const response = await api.get(`/api/reports/summary?${params.toString()}`);
    console.log('[Reports API] Resumo de relatórios recebido:', response.data);

    return response.data;
  } catch (error) {
    console.error('[Reports API] Erro ao buscar resumo de relatórios:', error);
    throw error;
  }
};

/**
 * Formata data para o padrão YYYY-MM usado pela API
 * @param date - Objeto Date
 * @returns string - Data formatada como YYYY-MM
 */
export const formatYearMonth = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Gera lista de meses anteriores para filtros
 * @param monthsCount - Número de meses para gerar
 * @returns Array<{label: string, value: string}> - Lista de meses formatados
 */
export const generateMonthOptions = (monthsCount: number = 12): Array<{label: string, value: string}> => {
  const options: Array<{label: string, value: string}> = [];
  const now = new Date();

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  for (let i = 0; i < monthsCount; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yearMonth = formatYearMonth(date);
    const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;

    options.push({
      label,
      value: yearMonth
    });
  }

  return options;
};

/**
 * Calcula percentual de uso em relação ao limite
 * @param used - Quantidade utilizada
 * @param limit - Limite máximo
 * @returns number - Percentual de uso (0-100)
 */
export const calculateUsagePercentage = (used: number, limit: number): number => {
  if (limit === 0) return used > 0 ? 100 : 0;
  return Math.min(Math.round((used / limit) * 100), 100);
};

/**
 * Determina cor do indicador baseado no percentual de uso
 * @param percentage - Percentual de uso
 * @returns string - Cor do indicador
 */
export const getUsageColor = (percentage: number): string => {
  if (percentage >= 90) return '#ef4444'; // vermelho
  if (percentage >= 70) return '#f59e0b'; // amarelo
  return '#10b981'; // verde
};

/**
 * Formata número para exibição
 * @param num - Número para formatar
 * @returns string - Número formatado com separadores
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('pt-BR').format(num);
};

/**
 * Busca relatório simplificado do mês vigente
 * @returns Promise<CurrentMonthReport> - Dados do mês vigente
 */
export const getCurrentMonthReport = async (): Promise<any> => {
  try {
    console.log('[Reports API] Buscando relatório do mês vigente');

    const params = new URLSearchParams();

    // Adiciona clinic_id do localStorage para filtrar pela clínica atual
    const clinicId = localStorage.getItem('clinic_id');
    if (clinicId) {
      params.append('clinic_id', clinicId);
      console.log('[Reports API] Filtrando por clinic_id:', clinicId);
    }

    const response = await api.get(`/api/reports/current-month?${params.toString()}`);
    console.log('[Reports API] Relatório do mês vigente recebido:', response.data);

    return response.data;
  } catch (error) {
    console.error('[Reports API] Erro ao buscar relatório do mês vigente:', error);
    throw error;
  }
};

/**
 * Busca consumo histórico acumulado e passos configurados nas automações.
 * @returns Promise<UsageOverviewResponse> - Dados históricos de consumo
 */
export const getUsageOverview = async (): Promise<UsageOverviewResponse> => {
  try {
    console.log('[Reports API] Buscando consumo histórico');

    const params = new URLSearchParams();
    const clinicId = localStorage.getItem('clinic_id');
    if (clinicId) {
      params.append('clinic_id', clinicId);
      console.log('[Reports API] Filtrando por clinic_id:', clinicId);
    }

    const response = await api.get(`/api/reports/usage-overview?${params.toString()}`);
    console.log('[Reports API] Consumo histórico recebido:', response.data);

    return response.data;
  } catch (error) {
    console.error('[Reports API] Erro ao buscar consumo histórico:', error);
    throw error;
  }
};

/**
 * Formata data para exibição
 * @param dateString - Data em string ISO
 * @returns string - Data formatada
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
